import { debounce } from 'lodash';
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useWebSocket from 'react-use-websocket';

import {
    IRFIConversation,
    Sender,
    createRFIConversationMessage,
    setLoadingState,
    setUnreadMessages,
    updateRFIConversationMessage,
  } from '../../slices/rfiMessageSlice';
import useDecryption from '../cryptography/decryptText';
import useEncryption from '../cryptography/encryptText';
import { useAppDispatch } from '../global/typedReduxHooks';

import requestInformationService from '~/services/requestInformationService';
import { RootState } from '~/utils/store';

interface WebSocketMessageFromAPI {
    id: string;
    from_user: number;
    to_user: number;
    message: string;
    is_translatable: boolean;
    created_at: string;
    rfi_conversation: IRFIConversation;
}

interface WebsocketRFIMessagesProps {
    conversation: string;
    sender: string;
    recipientId?: number;
    recipientRegion: string;
}

const useRFIWebSocket = ({
    conversation,
    sender,
    recipientRegion,
    recipientId,
} : WebsocketRFIMessagesProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(0);
  const { decryptText } = useDecryption();
  const region = useSelector((state: RootState) => state.auth.user.region);
  const { rfiConversations, activeRFIConversation } = useSelector((state: RootState) => state.rfiMessage);
  const { encryptText } = useEncryption();

  const { sendMessage, lastMessage, readyState } = useWebSocket(
    `${import.meta.env.VITE_WSS_PROTOCOL}${import.meta.env.VITE_WS_PATH}/report-rfi-chat/${conversation}/`,
    {
      shouldReconnect: () => true,
      queryParams: {
        token: `${sender === Sender.CASE_MANAGER ? localStorage.getItem('ws_access') : localStorage.getItem('access_token')}`,
        region: sender === Sender.CASE_MANAGER ? region || 'eu' : recipientRegion,
      },
    },
  );

  const debouncedChatHistory = debounce(async (websocketData) => {
    const localizedYou = intl.formatMessage({ defaultMessage: 'You' });
    const localizedSender = intl.formatMessage({ defaultMessage: 'Sender' });
    const webSocketUserId = parseInt(websocketData.user_id, 10);
    let unreadMessage = 0;
    setIsLoading(true);
    dispatch(setLoadingState({ conversationId: conversation, isLoading: true }));

    setUserId(webSocketUserId);

    if (websocketData.message.length > 0) {
      const decryptedMessages = await Promise.all(websocketData.message.map(
        async ({
          message,
          from_user: fromUser,
          created_at: createdAt,
          id: messageId,
          rfi_conversation,
        }: WebSocketMessageFromAPI) => {
          unreadMessage = rfi_conversation.unread_messages_count ?? 0;
          const decryptedText = await decryptText(message);
          return {
            content: decryptedText || '',
            name: fromUser === webSocketUserId ? localizedYou : localizedSender,
            createdAt,
            messageId,
          };
        },
      ));

      dispatch(updateRFIConversationMessage({
        conversationID: conversation,
        messages: decryptedMessages,
      }));

      dispatch(setUnreadMessages({ conversationId: conversation, number: unreadMessage }));
    } else {
      dispatch(updateRFIConversationMessage({
        conversationID: conversation,
        messages: [],
      }));
    }

    setIsLoading(false);
    dispatch(setLoadingState({ conversationId: conversation, isLoading: false }));
  }, 500);

  const debouncedChatMessage = debounce(async (websocketData) => {
    const localizedYou = intl.formatMessage({ defaultMessage: 'You' });
    const localizedSender = intl.formatMessage({ defaultMessage: 'Sender' });

    const {
      message,
      from_user: fromUser,
      created_at: createdAt,
      id: messageId,
      rfi_conversation: rfiConversationNewMessage,
    } = websocketData;
    const decryptedText = await decryptText(message);

    if (activeRFIConversation === conversation) {
      dispatch(setUnreadMessages({ conversationId: conversation, number: 0 }));
      if (sender === Sender.CASE_MANAGER) {
        requestInformationService.rfiConversationIsRead(conversation);
      } else {
        requestInformationService.rfiConversationIsReadRecipient(conversation);
      }
    } else {
      dispatch(setUnreadMessages({ conversationId: conversation, number: rfiConversationNewMessage.unread_messages_count }));
    }

    dispatch(
      createRFIConversationMessage({
        conversationID: conversation,
        message: {
          content: decryptedText || '',
          name: fromUser === userId ? localizedYou : localizedSender,
          createdAt,
          messageId,
        },
      }),
    );
  }, 300);

  const handleSendMessage = (inputValue: string) => {
    if (!inputValue) return;

    encryptText(inputValue).then((encryptedText) => {
      const chat = {
        message: encryptedText,
        action: 'create',
        to_user: recipientId,
      };
      sendMessage(JSON.stringify(chat));
    });
  };

  useEffect(() => {
    const handleWebSocketData = async () => {
      if (lastMessage !== null) {
        const websocketData = JSON.parse(lastMessage.data);
        if (websocketData.type === 'chat.history') {
          debouncedChatHistory(websocketData);
        }
        if (websocketData.type === 'chat.message') {
          debouncedChatMessage(websocketData);
        }
      }
    };

    handleWebSocketData();

    return () => {
      debouncedChatHistory.cancel();
      debouncedChatMessage.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  return {
    rfiConversation: rfiConversations.find(item => item.id === conversation),
    handleSendMessage,
    readyState,
    isLoading,
  };
};

export default useRFIWebSocket;